<template>
  <header class="relative isolate flow-root text-white [&_a:hover]:text-gray-400 [&_a]:text-white [&_a]:underline">
    <div
      data-testid="collection-header-image"
      class="absolute inset-0 -z-20 bg-cover bg-center bg-no-repeat"
      :style="{ backgroundImage: `url('${imageKitUrl}')` }"
    />
    <div class="absolute inset-0 -z-10 bg-black/65" />
    <div :class="BOOTSTRAP_CONTAINER">
      <div class="xl:w-10/12">
        <h1 class="mb-[27px] mt-9 flex flex-wrap items-center text-display-lg font-medium leading-[1.15]">
          <span class="mr-5 bg-orange-50 p-2 text-text-2xl uppercase text-base-defaultText leading-[1.15]">collection</span> <span class="max-w-full flex-1 break-words">{{ name }}</span>
        </h1>

        <div
          :id="descriptionId"
          ref="description"
          class="overflow-hidden transition-[max-height] duration-300 motion-reduce:transition-none"
          :class="{ 'max-h-16': !expanded }"
          :style="{ maxHeight: expandedMaxHeight }"
          @transitionend.self="uncapExpandedDescription"
        >
          <slot name="description" />
        </div>
        <div class="pb-[27px] pt-[9px] text-text-xl font-medium">
          <button
            type="button"
            class="text-white underline hover:text-gray-400"
            :aria-expanded="expanded"
            :aria-controls="descriptionId"
            @click="toggleExpanded"
          >{{ expanded ? 'Collapse' : 'Expand to Read More' }} <span
            class="material-icons-round align-middle transition-transform duration-300 motion-reduce:transition-none"
            :class="{ 'rotate-180': expanded }"
            aria-hidden="true"
          >expand_more</span></button>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, useTemplateRef } from 'vue'
import { urlFromAWSUrl } from '@/imagekit/imageurl_from_aws_url'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import { BOOTSTRAP_CONTAINER } from './bootstrap_container'

const props = defineProps<{
  name: string
  imageUrl: string
}>()

const descriptionId = 'collection-description'
const analytics = getAnalyticsTracker()
const expanded = ref(false)
const description = useTemplateRef<HTMLElement>('description')
const expandedMaxHeight = ref<string>()

const imageKitUrl = computed(() => urlFromAWSUrl(props.imageUrl, [{ width: 800, height: 800, crop: 'at_max' }]))

const fullDescriptionHeight = () => `${description.value?.scrollHeight ?? 0}px`

function prefersReducedMotion() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
}

function uncapExpandedDescription() {
  if (expanded.value) {
    expandedMaxHeight.value = 'none'
  }
}

function expand() {
  expanded.value = true
  expandedMaxHeight.value = fullDescriptionHeight()
  if (prefersReducedMotion()) {
    uncapExpandedDescription()
  }
  analytics.sendEvent(EVENT_NAMES.CURATED_COLLECTION.EXPANDED_DESCRIPTION)
}

// max-height cannot animate from none, so pin the current height for one frame before collapsing.
async function collapse() {
  expandedMaxHeight.value = fullDescriptionHeight()
  await nextTick()
  void description.value?.offsetHeight
  expanded.value = false
  expandedMaxHeight.value = undefined
}

function toggleExpanded() {
  if (expanded.value) {
    collapse()
  } else {
    expand()
  }
}
</script>
